import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import usePagedList from "../hooks/usePagedList";
import { statusBadgeClass } from "../utils/statusBadge";
import "./AdminConnectors.css";

const STATUS_OK = "ok";
const STATUS_NOT_CONFIGURED = "not_configured";
const STATUS_ERROR = "error";

/** Badge class for a runtime status.
 *
 * Only an explicit "ok" earns the success tone; "not_configured" stays the
 * neutral badge and EVERYTHING else — "error" and any unknown or malformed
 * value the API might send — falls back to the danger tone. An unreachable
 * system must never look healthy. */
function badgeClassFor(status) {
  if (status === STATUS_OK) return statusBadgeClass(STATUS_OK);
  if (status === STATUS_NOT_CONFIGURED) return statusBadgeClass(STATUS_NOT_CONFIGURED);
  return statusBadgeClass(STATUS_ERROR);
}

/** Localized label for a runtime status. The TONE comes from the badge class,
 *  the label only says which state the system is in. */
function labelFor(status, t) {
  if (status === STATUS_OK) return t("adminConnectors.runtimeStatusOk");
  if (status === STATUS_NOT_CONFIGURED) return t("adminConnectors.runtimeStatusNotConfigured");
  return t("adminConnectors.runtimeStatusError");
}

/** The detail is a server string — shown verbatim, never translated. */
const detailOf = (item) => {
  const detail = item && item.health ? item.health.detail : "";
  return typeof detail === "string" ? detail : "";
};

const nameOf = (item) => {
  if (!item) return "";
  if (typeof item.name === "string" && item.name) return item.name;
  return typeof item.id === "string" ? item.id : "";
};

const statusOf = (item) => (item && typeof item.status === "string" ? item.status : "");

/** Read-only "Runtime systems" section: the platform's own databases and
 *  models with the status the health probe reported for them.
 *
 *  The list is a cursor-paged read of `GET /api/v1/runtime-connections`; the
 *  component never probes anything itself and never offers a configuration
 *  control — runtime rows are read-only by design. */
export default function RuntimeConnections({ onError }) {
  const { t } = useTranslation();
  const { items, loading, error, hasMore, loadMore, reload } = usePagedList("/api/v1/runtime-connections");

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    if (error && typeof onErrorRef.current === "function") onErrorRef.current(error);
  }, [error]);

  return (
    <section className="cx-runtime" aria-label={t("adminConnectors.runtimeTitle")}>
      <div className="cx-runtime-head">
        <div>
          <h3 className="ds-section-title ds-flush">{t("adminConnectors.runtimeTitle")}</h3>
          <p className="ds-meta ds-flush">{t("adminConnectors.runtimeSubtitle")}</p>
        </div>
        <button
          type="button"
          className="ds-btn ds-btn--secondary ds-btn--sm"
          onClick={() => reload()}
          disabled={loading}
        >
          {t("adminConnectors.refresh")}
        </button>
      </div>

      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}

      {loading && items.length === 0 ? (
        <div className="ds-empty">{t("adminConnectors.loading")}</div>
      ) : items.length === 0 ? (
        <div className="ds-empty">{t("adminConnectors.runtimeEmpty")}</div>
      ) : (
        <ul className="cx-runtime-grid">
          {items.map((item, index) => {
            const id = item && typeof item.id === "string" ? item.id : `runtime-${index}`;
            const status = statusOf(item);
            const label = labelFor(status, t);
            const detail = detailOf(item);
            return (
              <li key={id} className="cx-runtime-tile" data-testid={`runtime-row-${id}`}>
                <span className="cx-runtime-name">{nameOf(item)}</span>
                <span data-testid={`runtime-status-${id}`} className={badgeClassFor(status)}>{label}</span>
                {/* the server detail adds nothing when it only repeats the status */}
                {detail && detail.toLowerCase() !== label.toLowerCase() && (
                  <span className="ds-meta cx-runtime-detail">{detail}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <button
          type="button"
          className="ds-btn ds-btn--secondary ac-load-more"
          onClick={() => loadMore()}
          disabled={loading}
        >
          {t("adminConnectors.loadMore")}
        </button>
      )}
    </section>
  );
}
